import { useBeginMfaEnrollment, useCompleteMfaEnrollment } from '@hooks/useMfa';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, Check, Copy, Download, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface MfaEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'setup' | 'verify' | 'recovery';

export function MfaEnrollmentDialog({ open, onOpenChange }: MfaEnrollmentDialogProps) {
  const [step, setStep] = useState<Step>('setup');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  const beginEnrollment = useBeginMfaEnrollment();
  const completeEnrollment = useCompleteMfaEnrollment();

  // Begin enrollment when dialog opens
  useEffect(() => {
    if (open) {
      setStep('setup');
      setCode('');
      setRecoveryCodes([]);
      completeEnrollment.reset();
      beginEnrollment.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    completeEnrollment.mutate(trimmedCode, {
      onSuccess: data => {
        setRecoveryCodes(data.recovery_codes || []);
        setStep('recovery');
      },
    });
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digitsOnly);
    if (completeEnrollment.isError) {
      completeEnrollment.reset();
    }
  };

  const handleCopySecret = () => {
    if (beginEnrollment.data?.secret) {
      navigator.clipboard.writeText(beginEnrollment.data.secret);
      toast.success('Secret copied to clipboard');
    }
  };

  const handleCopyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Recovery codes copied to clipboard');
  };

  const handleDownloadRecoveryCodes = () => {
    const content = recoveryCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compass-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Recovery codes downloaded');
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      // Clear sensitive data from mutation cache on dialog close
      beginEnrollment.reset();
      completeEnrollment.reset();
      setStep('setup');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        {step === 'setup' && (
          <>
            <DialogHeader>
              <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
              <DialogDescription>
                Scan the QR code below with your authenticator app (Google Authenticator, Authy,
                etc.)
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {beginEnrollment.isPending ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : beginEnrollment.isError ? (
                <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium">Setup Failed</p>
                      <p className="mt-1">
                        {getErrorMessage(beginEnrollment.error) ||
                          'Failed to begin enrollment. Please try again.'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {beginEnrollment.data?.provisioning_uri && (
                    <div className="flex justify-center">
                      <div className="rounded-lg border bg-white p-4">
                        <QRCodeSVG
                          value={beginEnrollment.data.provisioning_uri}
                          size={200}
                          level="M"
                        />
                      </div>
                    </div>
                  )}

                  {beginEnrollment.data?.secret && (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Or enter this secret manually:
                      </p>
                      <div
                        className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 cursor-pointer hover:bg-muted/80"
                        role="button"
                        tabIndex={0}
                        onClick={handleCopySecret}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') handleCopySecret();
                        }}
                      >
                        <code className="flex-1 text-sm font-mono break-all">
                          {beginEnrollment.data.secret}
                        </code>
                        <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </div>
                    </div>
                  )}
                </>
              )}

              <Button
                className="w-full"
                onClick={() => setStep('verify')}
                disabled={beginEnrollment.isPending || beginEnrollment.isError}
              >
                Next
              </Button>
            </div>
          </>
        )}

        {step === 'verify' && (
          <>
            <DialogHeader>
              <DialogTitle>Verify Setup</DialogTitle>
              <DialogDescription>
                Enter the 6-digit code from your authenticator app to complete setup.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleVerify} className="space-y-6 py-4">
              <Input
                value={code}
                onChange={handleCodeChange}
                placeholder="Enter 6-digit code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className={`h-12 text-center text-lg tracking-widest ${
                  completeEnrollment.isError
                    ? 'border-destructive focus-visible:ring-destructive'
                    : ''
                }`}
              />

              {completeEnrollment.isError && (
                <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium">Verification Failed</p>
                      <p className="mt-1">
                        {getErrorMessage(completeEnrollment.error) ||
                          'Invalid code. Please try again.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setStep('setup');
                    setCode('');
                    completeEnrollment.reset();
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={code.length < 6 || completeEnrollment.isPending}
                >
                  {completeEnrollment.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === 'recovery' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                Two-Factor Authentication Enabled
              </DialogTitle>
              <DialogDescription>
                Save these recovery codes in a safe place. You won&apos;t see them again. Each code
                can only be used once.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted p-4">
                {recoveryCodes.map((codeItem, index) => (
                  <code key={index} className="text-sm font-mono">
                    {codeItem}
                  </code>
                ))}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleCopyRecoveryCodes}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy All
                </Button>
                <Button variant="outline" className="flex-1" onClick={handleDownloadRecoveryCodes}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md text-sm dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200">
                <p className="font-medium">Important</p>
                <p className="mt-1">
                  If you lose access to your authenticator app, you can use these codes to sign in.
                  Store them securely.
                </p>
              </div>

              <Button className="w-full" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
